import {
  InfiniteData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient
} from "@tanstack/react-query";
import {
  IBaseMatchData,
  ICancelMatchData,
  IMatch,
  IUpdateCheckoutDatePayload,
  TAggregatedMatchData,
  TAggregatedMatchesResponse,
  THostActionExtrasPayload,
  TMatchData,
  TReview,
  TUpdatedMatchPayload
} from "../../interfaces/interfaces";
import { HostAPI } from "../hostApi";
import { getMatchQueryKey } from "./studentApiQueries";
import { getHouseQueryKey, getHousesQueryKey } from "./houseApiQueries";
import { useContextHospi } from "../../context/ContextHospi";
import {
  MatchStatusEnum,
  SHOWN_MATCHES_FROM_STATUS
} from "../../utils/constants";
import { getReviewsByHostQueryKey } from "./userApiQueries";
import { isoDate } from "../../utils/helpers";

export const useSendInvite = () => {
  const queryClient = useQueryClient();

  return useMutation<unknown, unknown, TUpdatedMatchPayload>({
    mutationFn: (payload) => HostAPI.invite(payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: getMatchQueryKey({
          studentId: variables.student_id,
          hostId: variables.host_id,
          houseId: variables.house_id
        })
      });
    }
  });
};
export const useCancelInvite = () => {
  const queryClient = useQueryClient();
  return useMutation<unknown, unknown, IBaseMatchData>({
    mutationFn: (payload) => HostAPI.cancelInvite(payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: getMatchQueryKey({
          studentId: variables.student_id,
          hostId: variables.host_id,
          houseId: variables.house_id
        })
      });
    }
  });
};
export const useCancelMatch = () => {
  const queryClient = useQueryClient();
  const { cognitoUser } = useContextHospi();

  return useMutation<
    unknown,
    unknown,
    { payload: ICancelMatchData; matchStatus: MatchStatusEnum }
  >({
    mutationFn: ({ payload }) => HostAPI.cancelMatch(payload),
    onSuccess: (_, { payload, matchStatus }) => {
      const updatedMatchData = {
        match_status:
          matchStatus === MatchStatusEnum.ACCEPTED
            ? MatchStatusEnum.CHATTED
            : MatchStatusEnum.ARCHIVED,
        offered_check_in_date: undefined,
        offered_check_out_date: undefined,
        most_recent_cancel_reason: payload.reason,
        student_action_extras: null,
        host_action_extras: null
      };

      queryClient.setQueryData<TMatchData[]>(
        getHostMatchesQueryKey({ userId: cognitoUser?.id }),
        (oldData) => {
          if (oldData) {
            return oldData.map((m) => {
              if (
                m.student_id === payload.student_id &&
                m.house_id === payload.house_id
              ) {
                return { ...m, ...updatedMatchData };
              } else return m;
            });
          }
        }
      );
      queryClient.setQueryData<InfiniteData<TAggregatedMatchesResponse>>(
        getHostAggregatedMatchesQueryKey({
          userId: cognitoUser?.id
        }),
        (oldData) => {
          if (oldData) {
            return {
              ...oldData,
              pages: oldData.pages.map((page) => ({
                ...page,
                data: page.data.reduce((acc, c) => {
                  if (
                    c.match.student_id === payload.student_id &&
                    c.match.house_id === payload.house_id
                  ) {
                    acc.push({
                      ...c,
                      match: { ...c.match, ...updatedMatchData }
                    });
                  } else acc.push(c);
                  return acc;
                }, [] as TAggregatedMatchData[])
              }))
            };
          }
        }
      );
      queryClient.invalidateQueries({
        queryKey: getHouseQueryKey(payload.house_id)
      });
      queryClient.invalidateQueries({
        queryKey: getHousesQueryKey(cognitoUser?.id!)
      });
    }
  });
};

export const getHostMatchesQueryKey = ({ userId }: { userId?: string }) => [
  "hostMatches",
  userId
];

export const useUpdateOfferedDates = (matchStatus?: MatchStatusEnum) => {
  const queryClient = useQueryClient();
  const { currentUserId } = useContextHospi();

  return useMutation<unknown, unknown, TUpdatedMatchPayload>({
    mutationFn: (payload) => HostAPI.updateOfferedDates(payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: getMatchQueryKey({
          studentId: variables.student_id,
          hostId: variables.host_id,
          houseId: variables.house_id
        })
      });
      if (matchStatus !== MatchStatusEnum.INVITED) {
        queryClient.setQueryData<TMatchData[]>(
          getHostMatchesQueryKey({ userId: currentUserId }),
          (oldData) => {
            if (oldData) {
              return oldData.map((m) => {
                if (
                  m.student_id === variables.student_id &&
                  m.house_id === variables.house_id
                ) {
                  return {
                    ...m,
                    check_in_date: variables.student_check_in_date,
                    check_out_date: variables.student_check_out_date
                  };
                } else return m;
              });
            }
          }
        );
        queryClient.setQueryData<InfiniteData<TAggregatedMatchesResponse>>(
          getHostAggregatedMatchesQueryKey({
            userId: currentUserId
          }),
          (oldData) => {
            if (oldData) {
              return {
                ...oldData,
                pages: oldData.pages.map((page) => ({
                  ...page,
                  data: page.data.reduce((acc, c) => {
                    if (
                      c.match.student_id === variables.student_id &&
                      c.match.house_id === variables.house_id
                    ) {
                      acc.push({
                        ...c,
                        match: {
                          ...c.match,
                          check_in_date: variables.student_check_in_date,
                          check_out_date: variables.student_check_out_date
                        }
                      });
                    } else acc.push(c);
                    return acc;
                  }, [] as TAggregatedMatchData[])
                }))
              };
            }
          }
        );
        queryClient.invalidateQueries({
          queryKey: getHouseQueryKey(variables.house_id)
        });
        queryClient.invalidateQueries({
          queryKey: getHousesQueryKey(currentUserId)
        });
      }
    }
  });
};

export const useGetHostMatches = (
  userId?: string,
  enabled: boolean = true,
  selectFn?: (res: TMatchData[]) => TMatchData[]
) => {
  return useQuery(
    getHostMatchesQueryKey({ userId }),
    () => {
      if (!userId) return;
      return HostAPI.getHostMatches(userId);
    },
    {
      enabled: Boolean(userId) && enabled,
      select: (data) =>
        typeof selectFn === "function" && Array.isArray(data)
          ? selectFn(data)
          : data
    }
  );
};

export const useGetHostMatch = (
  studentId?: string,
  hostId?: string,
  houseId?: string,
  enabled?: boolean
) => {
  return useQuery(
    getMatchQueryKey({ studentId, hostId, houseId }),
    () => {
      if (!studentId || !hostId || !houseId) return;

      return HostAPI.getMatch(studentId, hostId, houseId, {
        showNotificationOnError: false
      });
    },
    {
      enabled: Boolean(studentId && hostId && houseId) && !!enabled
    }
  );
};

export const getHostAggregatedMatchesQueryKey = ({
  userId
}: {
  userId?: string;
}) => ["hostAggregatedMatches", userId];

export const useGetHostAggregatedMatches = (
  enabled: boolean = true,
  selectFn?: (res: TAggregatedMatchData[]) => TAggregatedMatchData[]
) => {
  const { currentUserId } = useContextHospi();

  return useInfiniteQuery({
    queryKey: getHostAggregatedMatchesQueryKey({ userId: currentUserId }),
    queryFn: ({ pageParam }) => {
      return HostAPI.getAggregatedMatches(
        currentUserId!,
        pageParam ?? null,
        SHOWN_MATCHES_FROM_STATUS
      );
    },
    enabled: Boolean(currentUserId) && enabled,
    getNextPageParam: (lastPage) => lastPage.next_batch_token ?? undefined,
    select: (data) =>
      typeof selectFn === "function"
        ? {
            ...data,
            pages: data.pages.map((p) => {
              return Array.isArray(p.data)
                ? {
                    ...p,
                    data: selectFn(p.data)
                  }
                : p;
            })
          }
        : data
  });
};

export const useSetHostActions = () => {
  const queryClient = useQueryClient();
  const { currentUserId } = useContextHospi();

  return useMutation<unknown, unknown, { payload: THostActionExtrasPayload }>({
    mutationFn: ({ payload }) => HostAPI.setActions(payload),
    onSuccess: (_, { payload }) => {
      queryClient.invalidateQueries({
        queryKey: getHostMatchesQueryKey({ userId: currentUserId })
      });
      queryClient.setQueryData<InfiniteData<TAggregatedMatchesResponse>>(
        getHostAggregatedMatchesQueryKey({ userId: currentUserId }),
        (oldData) => {
          if (oldData) {
            return {
              ...oldData,
              pages: oldData.pages.map((page) => ({
                ...page,
                data: page.data.reduce((acc, c) => {
                  if (
                    c.match.house_id === payload.house_id &&
                    c.match.student_id === payload.student_id
                  ) {
                    acc.push({
                      ...c,
                      match: { ...c.match, host_action_extras: payload.data }
                    });
                  } else acc.push(c);
                  return acc;
                }, [] as TAggregatedMatchData[])
              }))
            };
          }
        }
      );
    }
  });
};

export const useUpdateCheckoutDateByHost = () => {
  const queryClient = useQueryClient();
  const { currentUserId } = useContextHospi();

  return useMutation<unknown, unknown, IUpdateCheckoutDatePayload>({
    mutationFn: (payload) => HostAPI.updateCheckoutDate(payload),
    onSuccess: (_, { student_id, host_id, house_id, new_check_out_date }) => {
      const updatedMatchData: Pick<
        TMatchData,
        | "check_out_date"
        | "checkout_date_actor"
        | "match_status"
        | "premature_archive_date"
      > = {
        check_out_date: new_check_out_date,
        checkout_date_actor: "host"
      };

      const todayISODate = isoDate(new Date());
      if (new_check_out_date <= todayISODate) {
        updatedMatchData.match_status = MatchStatusEnum.ARCHIVED;
        updatedMatchData.premature_archive_date = todayISODate;
      }

      queryClient.setQueryData<IMatch>(
        getMatchQueryKey({
          studentId: student_id,
          hostId: host_id,
          houseId: house_id
        }),
        (oldData) => {
          if (oldData) return { ...oldData, ...updatedMatchData };
        }
      );
      queryClient.setQueryData<TMatchData[]>(
        getHostMatchesQueryKey({ userId: currentUserId }),
        (oldData) => {
          if (oldData) {
            return oldData.map((m) => {
              if (m.student_id === student_id && m.house_id === house_id) {
                return { ...m, ...updatedMatchData };
              } else return m;
            });
          }
        }
      );
      queryClient.invalidateQueries({
        queryKey: getHostAggregatedMatchesQueryKey({ userId: currentUserId })
      });
      queryClient.invalidateQueries({
        queryKey: getHouseQueryKey(house_id)
      });
      queryClient.invalidateQueries({
        queryKey: getHousesQueryKey(currentUserId)
      });
    }
  });
};

export const useToggleIsReviewApproved = () => {
  const queryClient = useQueryClient();

  return useMutation<
    TReview,
    unknown,
    { payload: Pick<TReview, "approved" | "created_at">; hostId: string }
  >({
    mutationFn: ({ payload, hostId }) =>
      HostAPI.toggleIsReviewApproved(hostId, payload),
    onSuccess: (data, { payload, hostId }) => {
      queryClient.setQueryData<TReview[]>(
        getReviewsByHostQueryKey({ hostId }),
        (oldData) => {
          if (oldData) {
            return oldData.map((r) => {
              if (
                r.created_at === payload.created_at &&
                r.student_id === data.student_id &&
                r.house_id === data.house_id
              ) {
                return {
                  ...r,
                  approved: payload.approved
                };
              } else return r;
            });
          }
        }
      );
    }
  });
};
